import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Bell,
  Check,
  Loader2,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Wifi,
  WifiOff,
  Info,
  Cpu,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { DeleteConfirmDialog } from '@/components/ui/delete-confirm-dialog';
import { Badge } from '@/components/ui/Badge';
import { Separator } from '@/components/ui/separator';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import type { Column } from '@/components/ui/data-table';
import { useNotificationsPage } from '@/hooks/useNotificationsPage';
import { Notification, NotificationType } from '@/services/notificationService';
import { getNotificationStyle } from '@/hooks/useNotifications';
import { getNotificationNavUrl } from '@/services/notificationNav';

// ── helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<NotificationType, string> = {
  execution_complete:       'Execution Complete',
  execution_failed:         'Execution Failed',
  esim_provisioning_failure:'eSIM Provisioning',
  schedule_triggered:       'Schedule',
  device_offline:           'Device Offline',
  device_online:            'Device Online',
  system:                   'System',
};

const TYPE_ICONS: Record<NotificationType, React.ComponentType<{ className?: string }>> = {
  execution_complete:        CheckCircle,
  execution_failed:          XCircle,
  esim_provisioning_failure: Cpu,
  schedule_triggered:        Clock,
  device_offline:            WifiOff,
  device_online:             Wifi,
  system:                    Info,
};

// ── page ─────────────────────────────────────────────────────────────────────

export function NotificationsPage() {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    loadingMore,
    hasMore,
    unreadCount,
    unreadOnly,
    setUnreadOnly,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotificationsPage();

  const [typeFilter, setTypeFilter] = useState<NotificationType | 'all'>('all');
  // Notification waiting for confirmation in the DeleteConfirmDialog
  const [pendingDelete, setPendingDelete] = useState<Notification | null>(null);

  const visibleNotifications = useMemo(
    () =>
      typeFilter === 'all'
        ? notifications
        : notifications.filter((n) => n.type === typeFilter),
    [notifications, typeFilter]
  );

  const columns: Column<Notification>[] = [
    {
      key: 'read',
      header: 'Status',
      width: '90px',
      sortable: true,
      render: (_, row) =>
        row.read ? (
          <span className="text-xs text-muted-foreground">Read</span>
        ) : (
          <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-accent-blue text-white">
            Unread
          </Badge>
        ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '160px',
      sortable: true,
      render: (_, row) => {
        const { color, bgColor } = getNotificationStyle(row.type);
        const Icon = TYPE_ICONS[row.type];
        return (
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex w-5 h-5 rounded-full ${bgColor} items-center justify-center flex-shrink-0`}>
              <Icon className={`w-3 h-3 ${color}`} />
            </span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {TYPE_LABELS[row.type]}
            </span>
          </div>
        );
      },
    },
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (_, row) => (
        <span className={`text-sm font-medium ${row.read ? 'text-muted-foreground' : 'text-foreground'}`}>
          {row.title}
        </span>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      sortable: true,
      render: (_, row) => (
        <span className="text-xs text-muted-foreground line-clamp-1">{row.message}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Time',
      width: '130px',
      sortable: true,
      render: (_, row) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatDistanceToNow(row.createdAt, { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'id',
      header: '',
      width: '48px',
      render: (_, row) => (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Delete notification"
          title="Delete notification (removes it for the whole organization)"
          onClick={(e) => {
            e.stopPropagation();
            setPendingDelete(row);
          }}
        >
          <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-accent-red" />
        </Button>
      ),
    },
  ];

  const handleRowClick = (row: Notification) => {
    if (!row.read) markAsRead(row.id);
    const url = getNotificationNavUrl(row);
    if (url) navigate(url);
  };

  return (
    <AppShell>
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-foreground">Notifications</h1>
          {unreadCount > 0 && (
            <Badge variant="default" className="bg-accent-blue text-white">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            className="gap-1.5"
            title="Marks the notifications loaded below as read"
          >
            <Check className="w-3.5 h-3.5" />
            Mark all read
          </Button>
        )}
      </div>

      <Separator />

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Read filter */}
        <div className="flex gap-1">
          <Button
            variant={!unreadOnly ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setUnreadOnly(false)}
          >
            All
          </Button>
          <Button
            variant={unreadOnly ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setUnreadOnly(true)}
          >
            Unread
            {unreadCount > 0 && (
              <span className="ml-1.5 text-[10px] bg-accent-blue text-white rounded-full px-1.5 py-0.5 leading-none">
                {unreadCount}
              </span>
            )}
          </Button>
        </div>

        {/* Type filter */}
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as NotificationType | 'all')}>
          <SelectTrigger className="h-8 w-48 text-sm">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {(Object.keys(TYPE_LABELS) as NotificationType[]).map((t) => (
              <SelectItem key={t} value={t}>
                {TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Bell className="w-10 h-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">
            {unreadOnly ? 'No unread notifications' : 'No notifications yet'}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {unreadOnly ? "You're all caught up" : "You'll see updates here when something happens"}
          </p>
        </div>
      ) : (
        <ResponsiveTable
          columns={columns}
          data={visibleNotifications}
          keyExtractor={(n) => n.id}
          onRowClick={handleRowClick}
          searchable
          searchPlaceholder="Search by title, message, or type..."
          paginated
          defaultPageSize={25}
          emptyMessage="No notifications match your search"
          className="[&_tr]:group"
        />
      )}

      {/* Load more */}
      {hasMore && !loading && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-w-32"
          >
            {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load more'}
          </Button>
        </div>
      )}
    </div>

    <DeleteConfirmDialog
      open={pendingDelete !== null}
      onClose={() => setPendingDelete(null)}
      itemName={pendingDelete?.title}
      details="It is removed for everyone in the organization."
      onConfirm={async () => {
        if (pendingDelete) await deleteNotification(pendingDelete.id);
      }}
    />
    </AppShell>
  );
}
